/** @import { ElementInput } from '../helpers.js'; */
/** @import { NodeFilterInput } from '../filters.js'; */
/** @import { NodeInput } from '../helpers.js'; */
/** @import { QueryInput } from '../helpers.js'; */

import { callDomMethod, getDomProperty, isDocument, isFragment, isShadow, isWindow, merge } from '@fr0st/core';
import { parseParams } from '../ajax/helpers.js';
import { getContext, getWindow } from '../config.js';
import { parseFilter, parseNode, parseNodes } from '../filters.js';

/**
 * Executes a command in the document context.
 * @param {string} command The command to execute.
 * @param {string} [value] The value to give the command.
 * @returns {boolean} Whether the command was executed.
 */
export function exec(command, value = null) {
    return callDomMethod(getContext(), 'execCommand', command, false, value);
}

/**
 * Gets the index of the first node relative to its parent.
 * @param {NodeInput} selector The input node(s), or a query selector string.
 * @returns {number|undefined} The index, or `undefined` if no node or parent matches.
 */
export function index(selector) {
    const node = parseNode(selector, {
        node: true,
    });

    const parent = node && getDomProperty(node, 'parentNode');

    if (!parent) {
        return;
    }

    return merge([], getDomProperty(parent, 'children')).indexOf(node);
}

/**
 * Gets the index of the first node matching a filter.
 * @param {NodeInput} selector The input node(s), or a query selector string.
 * @param {NodeFilterInput} [nodeFilter] The filter node(s), a query selector string or custom filter function.
 * @returns {number} The index.
 */
export function indexOf(selector, nodeFilter) {
    nodeFilter = parseFilter(nodeFilter);

    return parseNodes(selector, {
        node: true,
        fragment: true,
        shadow: true,
    }).findIndex(nodeFilter);
}

/**
 * Normalizes nodes (remove empty text nodes, and join adjacent text nodes).
 * @param {NodeInput} selector The input node(s), or a query selector string.
 */
export function normalize(selector) {
    const nodes = parseNodes(selector, {
        node: true,
        fragment: true,
        shadow: true,
        document: true,
    });

    for (const node of nodes) {
        callDomMethod(node, 'normalize');
    }
}

/**
 * Returns a serialized string containing names and values of all form nodes.
 * @param {NodeInput} selector The input node(s), or a query selector string.
 * @returns {string} The serialized string.
 */
export function serialize(selector) {
    return parseParams(
        serializeArray(selector).map(({ name, value }) => ({
            name: name.replace(/\r\n|\r|\n/g, '\r\n'),
            value: value.replace(/\r\n|\r|\n/g, '\r\n'),
        })),
    );
}

/**
 * Returns a serialized array containing names and values of all form nodes.
 * @param {NodeInput} selector The input node(s), or a query selector string.
 * @returns {Array<{name: string, value: string}>} The serialized entries.
 */
export function serializeArray(selector) {
    const nodes = parseNodes(selector, {
        fragment: true,
        shadow: true,
    }).flatMap((node) => {
        if (isFragment(node) || isShadow(node)) {
            return merge([], callDomMethod(node, 'querySelectorAll', 'input, select, textarea'));
        }

        if (callDomMethod(node, 'matches', 'form')) {
            return merge([], getDomProperty(node, 'elements'));
        }

        return [node];
    });

    const values = [];

    for (const node of nodes) {
        if (callDomMethod(
            node,
            'matches',
            ':not(input, select, textarea), :disabled, datalist *, ' +
            'input:is([type=button], [type=submit], [type=reset], [type=file], [type=image]), ' +
            'input:is([type=radio], [type=checkbox]):not(:checked)',
        )) {
            continue;
        }

        const name = callDomMethod(node, 'getAttribute', 'name');
        if (!name) {
            continue;
        }

        if (callDomMethod(node, 'matches', 'select')) {
            for (const option of node.selectedOptions) {
                if (option.matches(':disabled')) {
                    continue;
                }

                values.push(
                    {
                        name,
                        value: option.value || '',
                    },
                );
            }
        } else {
            values.push(
                {
                    name,
                    value: node.value || '',
                },
            );
        }
    }

    return values;
}

/**
 * Sorts nodes by their position in the document.
 * @param {QueryInput} selector The input node(s), or a query selector string.
 * @returns {Array<Node|Window>} The sorted nodes.
 */
export function sort(selector) {
    const { Node } = getWindow();

    return parseNodes(selector, {
        node: true,
        fragment: true,
        shadow: true,
        document: true,
        window: true,
    }).sort((node, other) => {
        if (isWindow(node)) {
            return 1;
        }

        if (isWindow(other)) {
            return -1;
        }

        if (isDocument(node)) {
            return 1;
        }

        if (isDocument(other)) {
            return -1;
        }

        if (isFragment(other)) {
            return 1;
        }

        if (isFragment(node)) {
            return -1;
        }

        const isNodeShadow = isShadow(node);
        const isOtherShadow = isShadow(other);

        if (isNodeShadow) {
            node = node.host;
        }

        if (isOtherShadow) {
            other = other.host;
        }

        const nodeConnected = getDomProperty(node, 'isConnected');
        const otherConnected = getDomProperty(other, 'isConnected');

        if (!nodeConnected || !otherConnected) {
            if (nodeConnected !== otherConnected) {
                if (isNodeShadow && !nodeConnected) {
                    return 1;
                }

                if (isOtherShadow && !otherConnected) {
                    return -1;
                }

                return nodeConnected ?
                    1 :
                    -1;
            }

            if (callDomMethod(node, 'getRootNode') !== callDomMethod(other, 'getRootNode')) {
                return 0;
            }
        }

        if (node === other) {
            return 0;
        }

        const pos = callDomMethod(node, 'compareDocumentPosition', other);

        if (pos & Node.DOCUMENT_POSITION_FOLLOWING || pos & Node.DOCUMENT_POSITION_CONTAINED_BY) {
            return -1;
        }

        if (pos & Node.DOCUMENT_POSITION_PRECEDING || pos & Node.DOCUMENT_POSITION_CONTAINS) {
            return 1;
        }

        return 0;
    });
}

/**
 * Returns the tag name (lowercase) of the first node.
 * @param {ElementInput} selector The input node(s), or a query selector string.
 * @returns {string|undefined} The node's lowercase tag name, or `undefined` if no element matches.
 */
export function tagName(selector) {
    const node = parseNode(selector);

    if (!node) {
        return;
    }

    return getDomProperty(node, 'tagName').toLowerCase();
}
